import { Collapse, Box, Table, TableHead, TableRow, TableCell, TableBody, Chip, Tooltip } from '@mui/material';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import React, { ReactElement } from 'react';
import { formatNumber } from '../../common/Extensions';
import { DimensionTypeSR, GoodsStatusSR, DimensionTypeDimensionSR } from '../../common/Types';
import { GoodsTableExitInfo, getColorForGoodsStatus } from './GoodsHelper';
import { DimensionType } from '../../api/ClientApi';
import { FormatDateText } from '../../common/datesHelper';
import { useAppSelector } from '../../hooks/hooks';
import { canSeeValuableInfo } from '../../redux/selectors/employeeSelector';

type GoodsTableCollapsibleExitInfoProps = {
    isCollapsed: boolean;
    dimensionType?: DimensionType;
    goodsExitInfo?: GoodsTableExitInfo[];
    isSiteTable: boolean;
};

type ExitInfoSubjectCellProps = {
    soldToClient?: string;
    situation?: string;
    reason?: string;
};

/**
 * Klijent (prodaja) ili situacija (ugradnja) je glavni tekst; napomena ide u tooltip.
 * Kod otpisa nema ni klijenta ni situacije, pa je razlog sam glavni tekst.
 */
function ExitInfoSubjectCell(props: ExitInfoSubjectCellProps): ReactElement {
    const { soldToClient, situation, reason } = props;
    const subject = soldToClient ?? situation;
    const note = reason?.trim();

    if (!subject) {
        return <>{note || '/'}</>;
    }

    if (!note) {
        return <>{subject}</>;
    }

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <span>{subject}</span>
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{note}</span>} enterTouchDelay={0} arrow>
                <NotesOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} aria-label="Napomena" />
            </Tooltip>
        </Box>
    );
}

export default function GoodsTableCollapsibleExitInfo(props: GoodsTableCollapsibleExitInfoProps): ReactElement {
    const { isCollapsed, dimensionType, goodsExitInfo, isSiteTable } = props;

    const canSeeMoney = useAppSelector(canSeeValuableInfo);

    const companyDetails = useAppSelector((state) => state.companyDetails.companyDetails);

    return (
        <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
                <Table size="small" aria-label="purchases">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">
                                {dimensionType ? DimensionTypeSR[dimensionType] : 'Količina'}
                            </TableCell>
                            <TableCell align="center">Klijent/Situacija/Razlog</TableCell>
                            {!isSiteTable && <TableCell align="center">Skladište</TableCell>}
                            {canSeeMoney && (
                                <TableCell align="center">
                                    Prodajna cena {companyDetails?.currency ? '(' + companyDetails.currency + ')' : ''}
                                </TableCell>
                            )}
                            <TableCell align="center">Datum izvršenja</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {goodsExitInfo?.map((gi) => (
                            <TableRow key={(gi.goodsStatus ? gi.goodsStatus.toString() : '/') + Math.random()}>
                                <TableCell align="center">
                                    {gi.isReserved ? (
                                        <Chip variant="outlined" label={'Rezervisano'} color="default" />
                                    ) : gi.goodsStatus ? (
                                        <Chip
                                            variant="outlined"
                                            label={GoodsStatusSR[gi.goodsStatus]}
                                            color={getColorForGoodsStatus(gi.goodsStatus)}
                                        />
                                    ) : (
                                        '/'
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {gi.goodsQuantity ? formatNumber(gi.goodsQuantity) : '/'}{' '}
                                    {dimensionType ? DimensionTypeDimensionSR[dimensionType] : 'kom.'}
                                </TableCell>
                                <TableCell align="center">
                                    <ExitInfoSubjectCell
                                        soldToClient={gi.soldToClient}
                                        situation={gi.situation}
                                        reason={gi.reason}
                                    />
                                </TableCell>
                                {!isSiteTable && <TableCell align="center">{gi.siteName ?? '/'}</TableCell>}
                                {canSeeMoney && <TableCell align="center">{gi.sellingPrice ?? '/'}</TableCell>}
                                <TableCell align="center">
                                    {!!gi.executionDate ? FormatDateText(gi.executionDate) : '/'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </Collapse>
    );
}
